import { Stack } from '@mui/material'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useAlertContext } from '@/contexts/AlertContext'
import { TextEditor } from '@/features/controller/configuration/components/textEditor/TextEditor'
import type { ControllerConfigType } from '@/features/controller/configuration/types'
import { validateConfigText } from '@/features/controller/configuration/utils/validateConfigText'
import CancelConfirmIconButtons from '@/features/site/components/CancelConfirmIconButtons'

interface ConfigurationBulkActionTextEditorProps {
  configType: ControllerConfigType
  onApplyChange: (value: string) => void
  value: string
}

export function ConfigurationBulkActionTextEditor({
  configType,
  onApplyChange,
  value,
}: Readonly<ConfigurationBulkActionTextEditorProps>) {
  const { t } = useTranslation()
  const { pushAlert } = useAlertContext()
  const [currentValue, setCurrentValue] = useState<string>('')
  const [isSchemaValid, setIsSchemaValid] = useState(true)

  useEffect(() => setCurrentValue(value), [value])

  function handleConfirmClick() {
    const { isValid, errorMessage } = validateConfigText(configType, currentValue, t)
    if (isValid) {
      onApplyChange(currentValue)
    } else {
      pushAlert({
        message: errorMessage!,
        severity: 'error',
      })
    }
  }

  return (
    <Stack direction="column" padding={2}>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2, mr: 1 }}>
        <CancelConfirmIconButtons
          cancelDisabled={value === currentValue}
          disabled={value === currentValue || !isSchemaValid}
          onCancelClick={() => setCurrentValue(value)}
          onConfimClick={handleConfirmClick}
        />
      </Stack>

      <TextEditor
        configTypeId={configType.id}
        language={configType.textEditorLanguage}
        value={currentValue}
        onChange={(newValue) => setCurrentValue(newValue || '')}
        onValidationChange={(isValid) => setIsSchemaValid(isValid)}
      />
    </Stack>
  )
}
